import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { MenuItem } from 'primereact/menuitem'
import { useTranslation } from 'react-i18next'
import { useAuth } from 'react-oidc-context'
import { assistantKeys, getAssistantState } from './api/assistantApi'
import { useCurrentUser } from '../../auth/useCurrentUser'

/**
 * "Asistan Tanımla" — sağ üstteki **kimlik menüsüne** (`PersonaBadge`) eklenen madde (126).
 *
 * <p>Yeri 089'un persona seçimiyle aynıdır ve bu bilinçlidir: isteğin kendisi *"sağ üst köşede
 * rumuzuna tıklayınca"* diyor, ayrıca kimlik ve onun adına çalışacak kişi aynı kavramsal yere ait.
 * Ayrı bir topbar düğmesi açmak, 089'da etiketsiz bir seçim kutusunun anlaşılmadığı için terk edilen
 * yola geri dönmek olurdu.
 *
 * <p><b>Uygun olmayan kullanıcıda boş dizi döner</b> — menüye hiçbir şey eklenmez, yani özellik
 * görünmez. Uygunluk sunucudan tek bir bayrak olarak gelir ({@link getAssistantState}); arayüz kadro
 * kodunu ya da yetkiyi kendisi yorumlamaz. Sunucu o bayrağı kadro koşulu **ve** aday arama yetkisini
 * birlikte değerlendirerek üretir, böylece "menüyü görüyorum ama arama reddediliyor" çıkmazı hiç
 * oluşmaz (fail-closed).
 *
 * <p>Diyalogu bu hook <b>render etmez</b>; yalnız açılış durumunu taşır. Menü maddesi ile diyalogun
 * ayrı tutulması, `PersonaBadge`'in asistandan habersiz kalmasını sağlar — o yalnız kendisine verilen
 * maddeleri çizer.
 */
export function useAssistantMenu(): {
  items: MenuItem[]
  dialogVisible: boolean
  closeDialog: () => void
} {
  const { t } = useTranslation()
  const auth = useAuth()
  const { data: me } = useCurrentUser()
  const [dialogVisible, setDialogVisible] = useState(false)

  // `/me` ile aynı koşul: kimlik yokken uygunluk sorulmaz.
  const authenticated = auth.isAuthenticated

  const { data: state } = useQuery({
    queryKey: assistantKeys.state(me?.userId),
    enabled: authenticated && me != null,
    queryFn: getAssistantState,
    // Oturum kapsamlı veri: uygunluk kadroya ve yetkiye bağlı, bir oturum içinde değişmez.
    // staleTime olmadan (proje varsayılanı 0 + TanStack v5'te refetchOnWindowFocus açık) her pencere
    // odağında bu uç çağrılıyordu ve uç harici SAP HR görünümünü okuyor — AssistantStateDto'nun
    // kendi gerekçesi alanı /me'ye koymamayı tam bu maliyetle reddediyordu, hook onu geri getiriyordu.
    staleTime: Infinity,
  })

  const items: MenuItem[] = state?.canDefine
    ? [
        {
          label: t('assistant.menu.define'),
          icon: 'pi pi-user-plus',
          command: () => setDialogVisible(true),
        },
      ]
    : []

  return { items, dialogVisible, closeDialog: () => setDialogVisible(false) }
}
